import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Clock, Route, ArrowRight } from "lucide-react";
import { client } from "../lib/config";
import PageHero from "../components/PageHero";
import Breadcrumbs from "../components/Breadcrumbs";
import BookCallCTA from "../components/BookCallCTA";
import AttractionMedia from "../components/AttractionMedia";

export default function LocalGuidePage() {
  const [attractions, setAttractions] = useState([]);
  const [towns, setTowns] = useState([]);
  const [ramps, setRamps] = useState([]);
  const [restaurants, setRestaurants] = useState([]);

  useEffect(() => {
    client.get("/attractions").then((r) => setAttractions(r.data)).catch(() => {});
    client.get("/nearby-towns").then((r) => setTowns(r.data)).catch(() => {});
    client.get("/truman-ramps").then((r) => setRamps(r.data)).catch(() => {});
    client.get("/clinton-restaurants").then((r) => setRestaurants(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <PageHero
        eyebrow="Local guide"
        title="Clinton, Missouri: the definitive local guide."
        intro="Truman Lake, the Katy Trail's western end, the Historic Downtown Square, the surrounding Henry County towns — everything you can actually verify. From the people who live here."
        image={null}
        metaTitle="Clinton MO Local Guide — Truman Lake, Katy Trail, Nearby Towns"
        metaDesc="A locals' guide to Clinton, Missouri: Truman Lake ramps, the Katy Trail western terminus, the historic downtown square, and every nearby town with real drive times."
      />
      <Breadcrumbs items={[{ label: "Local Guide" }]} />

      {/* TOC */}
      <section className="bg-[#FDFBF7]">
        <div className="wb-container pt-8 pb-4">
          <div className="wb-eyebrow mb-4">On this page</div>
          <div className="flex flex-wrap gap-2" data-testid="local-guide-toc">
            {attractions.map((a) => (
              <a
                key={a.slug}
                href={`#${a.slug}`}
                className="px-3 py-1.5 bg-[#F5F0E6] hover:bg-[#FCEEEA] border border-[#E5DFD3] text-sm text-[#1A1A1A] hover:text-[#B94A15] font-medium rounded-sm"
                data-testid={`toc-${a.slug}`}
              >
                {a.name}
              </a>
            ))}
            <a href="#nearby-towns" className="px-3 py-1.5 bg-[#F5F0E6] hover:bg-[#FCEEEA] border border-[#E5DFD3] text-sm text-[#1A1A1A] hover:text-[#B94A15] font-medium rounded-sm">Nearby Towns</a>
            <a href="#truman-ramps" className="px-3 py-1.5 bg-[#F5F0E6] hover:bg-[#FCEEEA] border border-[#E5DFD3] text-sm text-[#1A1A1A] hover:text-[#B94A15] font-medium rounded-sm">Truman Lake Ramps</a>
            <a href="#restaurants" className="px-3 py-1.5 bg-[#F5F0E6] hover:bg-[#FCEEEA] border border-[#E5DFD3] text-sm text-[#1A1A1A] hover:text-[#B94A15] font-medium rounded-sm">Restaurants</a>
          </div>
        </div>
      </section>

      {/* Attractions */}
      <section className="bg-[#FDFBF7]">
        <div className="wb-container py-16 md:py-20 space-y-16">
          {attractions.map((a, idx) => (
            <article key={a.slug} id={a.slug} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center scroll-mt-24" data-testid={`attraction-${a.slug}`}>
              <div className={`lg:col-span-6 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                {a.image ? (
                  <figure className="space-y-2">
                    <img
                      src={a.image}
                      alt={`${a.name} near Clinton, MO`}
                      className="w-full aspect-[4/3] object-cover"
                      loading="lazy"
                    />
                    {a.image_credit && (
                      <figcaption className="text-[10px] uppercase tracking-widest text-[#4A4641]">
                        Photo: {a.image_credit}
                      </figcaption>
                    )}
                  </figure>
                ) : (
                  <AttractionMedia name={a.name} category={a.category} />
                )}
              </div>
              <div className="lg:col-span-6">
                <div className="wb-eyebrow">{a.category}</div>
                <h2 className="wb-h2 mt-3">{a.name}</h2>
                <p className="wb-body mt-4">{a.summary}</p>
                <p className="wb-body mt-3 text-base">{a.detail}</p>
                <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-[#B94A15] mt-1" />
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-[#4A4641]">Drive time</div>
                      <div className="text-sm font-bold text-[#1A1A1A]">{a.drive_time_min} min</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Route className="h-4 w-4 text-[#B94A15] mt-1" />
                    <div>
                      <div className="text-xs font-bold uppercase tracking-wider text-[#4A4641]">Distance</div>
                      <div className="text-sm font-bold text-[#1A1A1A]">{a.drive_time_miles} mi</div>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-start gap-2 max-w-md">
                  <MapPin className="h-4 w-4 text-[#B94A15] mt-1" />
                  <div className="text-xs text-[#4A4641]">Route: {a.route}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Truman Lake Ramps */}
      <section id="truman-ramps" className="bg-[#F5F0E6] border-y border-[#E5DFD3] scroll-mt-24">
        <div className="wb-container py-16 md:py-20">
          <div className="max-w-2xl mb-10">
            <div className="wb-eyebrow">Truman Lake — ramps & marinas</div>
            <h2 className="wb-h2 mt-3">The three ramps most Westbridge guests actually use.</h2>
            <p className="wb-body mt-4">
              Truman Lake is 55,600 acres. You don't fish the whole thing. Here are the closest Grand River arm ramps and marinas from our front desk.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ramps.map((r) => (
              <article key={r.name} className="wb-card bg-white p-6" data-testid={`ramp-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <h3 className="wb-h3">{r.name}</h3>
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-[#B94A15]" />
                  <span className="font-bold text-[#1A1A1A]">~{r.from_hotel_min} min from Westbridge</span>
                </div>
                <p className="wb-body mt-4 text-sm">{r.detail}</p>
              </article>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/truman-lake-fishing" className="wb-btn-outline" data-testid="local-guide-fishing-link">
              Full angler guide <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Drive-time table & Nearby Towns */}
      <section id="nearby-towns" className="bg-[#FDFBF7] scroll-mt-24">
        <div className="wb-container py-16 md:py-20">
          <div className="max-w-2xl mb-10">
            <div className="wb-eyebrow">Nearby towns</div>
            <h2 className="wb-h2 mt-3">Drive times from Westbridge to every town in the neighborhood.</h2>
            <p className="wb-body mt-4">
              Coming from any of these towns? We're the closest full-service hotel with 32 renovated rooms and boat/truck parking.
            </p>
          </div>

          <div className="overflow-x-auto wb-card">
            <table className="w-full text-sm" data-testid="drive-time-table">
              <thead className="bg-[#1A1A1A] text-white text-left" style={{ fontFamily: "'Nunito', sans-serif" }}>
                <tr>
                  <th className="px-4 py-3 font-bold">Town</th>
                  <th className="px-4 py-3 font-bold">Miles</th>
                  <th className="px-4 py-3 font-bold">Drive time</th>
                  <th className="px-4 py-3 font-bold hidden md:table-cell">What's there</th>
                </tr>
              </thead>
              <tbody>
                {towns.map((t, i) => (
                  <tr key={t.slug} id={t.slug} className={`scroll-mt-24 border-t border-[#E5DFD3] ${i % 2 === 0 ? "bg-white" : "bg-[#FDFBF7]"}`} data-testid={`town-row-${t.slug}`}>
                    <td className="px-4 py-3 font-bold text-[#1A1A1A]" style={{ fontFamily: "'Nunito', sans-serif" }}>{t.name}</td>
                    <td className="px-4 py-3">{t.drive_time_miles} mi</td>
                    <td className="px-4 py-3">{t.drive_time_min} min</td>
                    <td className="px-4 py-3 text-[#4A4641] hidden md:table-cell">{t.summary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Restaurants */}
      <section id="restaurants" className="bg-[#F5F0E6] border-y border-[#E5DFD3] scroll-mt-24">
        <div className="wb-container py-16 md:py-20">
          <div className="max-w-2xl mb-10">
            <div className="wb-eyebrow">Where we send guests to eat</div>
            <h2 className="wb-h2 mt-3">Real restaurants on the historic downtown square.</h2>
            <p className="wb-body mt-4">
              Hours change — call before you go if timing matters. All four are within a five-minute walk of the Henry County Courthouse.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {restaurants.map((r) => (
              <article key={r.name} className="wb-card bg-white p-6" data-testid={`restaurant-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h3 className="wb-h3">{r.name}</h3>
                <p className="mt-1 text-sm text-[#4A4641]"><MapPin className="inline h-3.5 w-3.5 mr-1 text-[#B94A15]" />{r.address}</p>
                <p className="wb-body mt-3 text-sm">{r.note}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <BookCallCTA />
    </>
  );
}
